import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type FundingStep = "upload" | "preview" | "import";

const STEPS: { key: FundingStep; label: string; hint: string }[] = [
  { key: "upload", label: "Upload", hint: "Crunchbase CSV" },
  { key: "preview", label: "Review data", hint: "Check the rows" },
  { key: "import", label: "Import", hint: "Record and re-score" },
];

/** The three steps of a funding import, the current one highlighted. */
export function FundingStepper({ step }: { step: FundingStep }) {
  const current = STEPS.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-2" aria-label="Import steps">
      {STEPS.map((s, index) => {
        const done = index < current;
        const active = index === current;
        return (
          <li key={s.key} className="flex flex-1 items-center gap-2">
            <span
              aria-current={active ? "step" : undefined}
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                done && "bg-indigo-600 text-white",
                active && "bg-indigo-50 text-indigo-700 ring-2 ring-indigo-600",
                !done && !active && "bg-slate-100 text-slate-400",
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : index + 1}
            </span>
            <span className="min-w-0">
              <span
                className={cn(
                  "block text-sm font-medium",
                  active ? "text-slate-900" : done ? "text-slate-700" : "text-slate-400",
                )}
              >
                {s.label}
              </span>
              <span className="block text-[11px] text-slate-400">{s.hint}</span>
            </span>
            {index < STEPS.length - 1 && (
              <span
                className={cn(
                  "mx-1 hidden h-px flex-1 sm:block",
                  done ? "bg-indigo-600" : "bg-slate-200",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
